import { useCallback, useEffect, useRef, useState } from "react";
import {
  UNFILED,
  deleteConversation,
  generateSummary,
  getConversationMessages,
  getFolderInventory,
  listConversations,
  moveToFolder,
  renameConversation,
} from "./historyService";
import { exportConversationAsCsv } from "./exportConversation";

const PAGE_LIMIT = 15;

// ── The list ──────────────────────────────────────────────────────────────

/**
 * The conversation list: its filters, its paging, and the rows themselves.
 *
 * Every change to a row goes through here, because this is what knows which
 * page request is current; a slow response for a view the user has left must
 * not overwrite what they did since.
 */
export function useHistoryList({ currentUser, language, trackSearch }) {
  const [items, setItems] = useState([]);
  const cursorRef = useRef(null); // {ts, id} of last loaded item; drives "load more"
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true); // initial / filter reload
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");

  // Filters
  const [searchKeyword, setSearchKeyword] = useState("");
  const [filterDate, setFilterDate] = useState("");
  const [filterFolder, setFilterFolder] = useState("");

  // Guards against out-of-order responses when filters change quickly
  const requestRef = useRef(0);
  // Which view the user is on now, for saves that began in an earlier one.
  const viewKey = JSON.stringify([currentUser?.uid, searchKeyword, filterDate, filterFolder]);
  const viewRef = useRef(viewKey);
  viewRef.current = viewKey;
  const latestFetchRef = useRef(null);

  const fetchPage = useCallback(
    async ({ reset }) => {
      if (!currentUser) return;
      const reqId = ++requestRef.current;

      if (reset) setLoading(true);
      else setLoadingMore(true);
      setError("");

      try {
        const token = await currentUser.getIdToken();
        const data = await listConversations(token, {
          limit: PAGE_LIMIT,
          q: searchKeyword.trim(),
          date: filterDate,
          folder: filterFolder,
          cursor: reset ? null : cursorRef.current,
        });

        // Ignore responses superseded by a newer request
        if (reqId !== requestRef.current) return;

        setItems((prev) => (reset ? data.items : [...prev, ...data.items]));
        cursorRef.current = data.next_cursor;
        setHasMore(data.has_more);
        setTotal(data.total);
      } catch {
        if (reqId === requestRef.current) {
          setError(
            language === "zh"
              ? "載入歷史記錄失敗，請重試。"
              : "Failed to load history. Please try again.",
          );
        }
      } finally {
        if (reqId === requestRef.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    // cursor lives in a ref (cursorRef) so "load more" always reads the latest value
    [currentUser, searchKeyword, filterDate, filterFolder, language],
  );

  latestFetchRef.current = fetchPage;

  // Initial load + debounced reload whenever filters change
  useEffect(() => {
    if (!currentUser) return;
    // Invalidate the previous response immediately, including during debounce.
    ++requestRef.current;
    setLoading(true);
    setLoadingMore(false);
    const t = setTimeout(() => {
      if (searchKeyword.trim()) trackSearch(searchKeyword.trim());
      fetchPage({ reset: true });
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, searchKeyword, filterDate, filterFolder]);

  // Opening a folder ("" for all, UNFILED for none) starts a clean search.
  const showFolder = (folder) => {
    setFilterFolder(folder);
    setSearchKeyword("");
    setFilterDate("");
  };

  const clearFilters = () => {
    setSearchKeyword("");
    setFilterDate("");
  };

  // Mirrors the folder filter the server applied to build this page, so an
  // optimistic edit can tell whether a row still belongs in it.
  const matchesActiveFilters = (conv) => {
    if (filterFolder === UNFILED && conv.folder) return false;
    if (
      filterFolder &&
      filterFolder !== UNFILED &&
      (conv.folder || "").toLocaleLowerCase() !== filterFolder.toLocaleLowerCase()
    )
      return false;
    return true;
  };

  /**
   * Put a saved row where the server's ordering would: updated in place while
   * it still belongs in the view, gone once it doesn't, and back in date order
   * when Undo returns it. A row past the last loaded page is left for "Load
   * more", which would otherwise show it twice.
   */
  const placeRow = (list, row) => {
    if (!matchesActiveFilters(row))
      return list.filter((item) => item.session_id !== row.session_id);
    if (list.some((item) => item.session_id === row.session_id))
      return list.map((item) =>
        item.session_id === row.session_id ? { ...item, folder: row.folder } : item,
      );
    const at = list.findIndex(
      (item) =>
        item.created_at < row.created_at ||
        (item.created_at === row.created_at && item.session_id < row.session_id),
    );
    if (at !== -1) return [...list.slice(0, at), row, ...list.slice(at)];
    const cursor = cursorRef.current;
    const pastPage =
      hasMore &&
      cursor &&
      (row.created_at < cursor.ts ||
        (row.created_at === cursor.ts && row.session_id < cursor.id));
    return pastPage ? list : [...list, row];
  };

  /** Apply a folder move the server has accepted. */
  const applySavedRow = (before, after) => {
    // A response started before the move must not put the old row back.
    ++requestRef.current;
    setLoading(false);
    setLoadingMore(false);
    setItems((current) => placeRow(current, after));
    const shift = (matchesActiveFilters(after) ? 1 : 0) - (matchesActiveFilters(before) ? 1 : 0);
    if (shift) setTotal((count) => Math.max(0, count + shift));
  };

  const updateRow = (sessionId, fields) =>
    setItems((cur) => cur.map((c) => (c.session_id === sessionId ? { ...c, ...fields } : c)));

  /** Delete optimistically; returns whether the server agreed. */
  const remove = async (sessionId) => {
    const prevItems = items;
    setItems((cur) => cur.filter((c) => c.session_id !== sessionId));
    setTotal((t) => Math.max(0, t - 1));
    try {
      const token = await currentUser.getIdToken();
      await deleteConversation(token, sessionId);
      return true;
    } catch {
      setItems(prevItems); // rollback on failure
      setTotal((t) => t + 1);
      setError(
        language === "zh"
          ? "刪除失敗，請重試。"
          : "Delete failed. Please try again.",
      );
      return false;
    }
  };

  const rename = async (sessionId, title) => {
    const nextTitle = title.trim();
    const target = items.find((c) => c.session_id === sessionId);
    // Nothing changed — skip the round trip.
    if (!target || nextTitle === (target.title || "")) return;

    // The server stores "" as no title; the row shows that as null, which
    // makes it fall back to the summary or the first question.
    const prevItems = items;
    updateRow(sessionId, { title: nextTitle || null });
    try {
      const token = await currentUser.getIdToken();
      await renameConversation(token, sessionId, nextTitle);
    } catch {
      setItems(prevItems); // rollback on failure
      setError(
        language === "zh"
          ? "重新命名失敗，請重試。"
          : "Rename failed. Please try again.",
      );
    }
  };

  return {
    items,
    total,
    hasMore,
    loading,
    loadingMore,
    error,
    setError,
    searchKeyword,
    setSearchKeyword,
    filterDate,
    setFilterDate,
    filterFolder,
    showFolder,
    clearFilters,
    loadMore: () => fetchPage({ reset: false }),
    viewKey,
    isView: (key) => viewRef.current === key,
    reload: () => latestFetchRef.current({ reset: true }),
    applySavedRow,
    updateRow,
    remove,
    rename,
  };
}

// ── Folders ───────────────────────────────────────────────────────────────

/**
 * The folder list with its counts, and moving conversations between folders.
 *
 * The inventory comes from its own unfiltered request: the list's response
 * only names the folders that match its search and date filters.
 */
export function useFolders({ currentUser, language, list }) {
  const [folders, setFolders] = useState([]);
  // Conversations per folder, and in all. The total stays null until a load
  // succeeds, so the rail can leave counts off rather than show zeros.
  const [counts, setCounts] = useState({});
  const [total, setTotal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const requestRef = useRef(0);
  const userRef = useRef(currentUser);
  userRef.current = currentUser;

  // The row whose folder menu is open.
  const [menuFor, setMenuFor] = useState(null);
  const [moving, setMoving] = useState(false);
  const [moveError, setMoveError] = useState("");
  // The last move, for the toast and its Undo.
  const [notice, setNotice] = useState(null);

  const refresh = useCallback(async () => {
    if (!currentUser || userRef.current !== currentUser) return;
    const requestId = ++requestRef.current;
    const isCurrent = () => requestId === requestRef.current && userRef.current === currentUser;
    setLoading(true);
    setError(false);
    try {
      const token = await currentUser.getIdToken();
      const inventory = await getFolderInventory(token);
      if (!isCurrent()) return;
      setFolders(inventory.folders);
      setCounts(inventory.counts);
      setTotal(inventory.total);
    } catch {
      if (isCurrent()) setError(true);
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    userRef.current = currentUser;
    setFolders([]);
    setCounts({});
    setTotal(null);
    if (currentUser) refresh();
    else setLoading(false);
    return () => {
      userRef.current = null;
    };
  }, [currentUser, refresh]);

  // Shows a folder the server just created, before the refresh lands.
  const remember = (folder) => {
    if (!folder) return;
    setFolders((previous) =>
      previous.includes(folder)
        ? previous
        : [...previous, folder].sort((a, b) => a.localeCompare(b)),
    );
  };

  const toggleMenu = (sessionId) => {
    setMoveError("");
    setMenuFor((open) => (open === sessionId ? null : sessionId));
  };

  const closeMenu = () => setMenuFor(null);

  // Leaving the view closes the menu, and the error it was showing.
  const resetMenu = () => {
    setMenuFor(null);
    setMoveError("");
  };

  // `target` is the row as the caller has it. Undo passes the copy kept from
  // the move, since a row that left the open folder is no longer in the list.
  const move = async (target, folder, { undo = false } = {}) => {
    if (moving) return;
    if (folder === (target.folder || "")) {
      setMenuFor(null);
      return;
    }
    const startedInView = list.viewKey;
    const owner = currentUser.uid;
    setMoving(true);
    setMoveError("");
    if (!undo) setNotice(null);
    try {
      const token = await currentUser.getIdToken();
      const savedFolder = await moveToFolder(token, target.session_id, folder);
      if (userRef.current?.uid !== owner) return;
      const updated = { ...target, folder: savedFolder };
      if (list.isView(startedInView)) list.applySavedRow(target, updated);
      // The user navigated while saving; refresh their current destination.
      else list.reload();
      setMenuFor(null);
      remember(savedFolder);
      refresh();
      setNotice(
        undo
          ? { folder: savedFolder, undone: true }
          : { folder: savedFolder, previous: target.folder || "", row: updated },
      );
    } catch {
      if (userRef.current?.uid === owner) {
        const message = language === "zh" ? "移動失敗，請重試。" : "Could not move this conversation. Please try again.";
        setMoveError(message);
        // The menu shows the error for an ordinary move; Undo has no menu,
        // and a move started in another view has no row here to anchor it.
        if (undo || !list.isView(startedInView)) list.setError(message);
      }
    } finally {
      setMoving(false);
    }
  };

  const undo = () => {
    if (notice?.row) move(notice.row, notice.previous, { undo: true });
  };

  return {
    folders,
    counts,
    total,
    loading,
    error,
    refresh,
    menuFor,
    toggleMenu,
    closeMenu,
    resetMenu,
    moving,
    moveError,
    move,
    notice,
    undo,
    dismissNotice: () => setNotice(null),
  };
}

// ── Summaries ─────────────────────────────────────────────────────────────

/** Generating a conversation's LLM recap and putting it on its row. */
export function useSummaries({ currentUser, language, list }) {
  // Sessions whose summary is being generated. A list rather than one id: the
  // call takes seconds, and nothing stops someone starting a second one.
  const [summarizing, setSummarizing] = useState([]);

  const generate = async (sessionId, force = false) => {
    if (summarizing.includes(sessionId)) return;
    setSummarizing((cur) => [...cur, sessionId]);
    list.setError("");
    try {
      const token = await currentUser.getIdToken();
      const summary = await generateSummary(token, sessionId, force);
      list.updateRow(sessionId, { summary });
    } catch {
      list.setError(
        language === "zh"
          ? "產生摘要失敗，請重試。"
          : "Could not generate the summary. Please try again.",
      );
    } finally {
      setSummarizing((cur) => cur.filter((id) => id !== sessionId));
    }
  };

  return { summarizing, generate };
}

// ── Transcripts ───────────────────────────────────────────────────────────

/**
 * Full conversations: fetched on demand, shared by expanding a row and
 * exporting it, and fetched once each.
 */
export function useTranscripts({ currentUser, language, onError }) {
  // The open row, and each loaded transcript: sessionId -> { loading, messages, error }
  const [expanded, setExpanded] = useState(null);
  const [details, setDetails] = useState({});
  // Sessions whose transcript is being fetched for an export. Exporting a
  // collapsed row has to fetch it first, and that is a visible wait.
  const [exporting, setExporting] = useState([]);
  // The conversation being saved as a PDF: { conv, messages }. The print view
  // renders it and opens the print dialog once it is on the page.
  const [printing, setPrinting] = useState(null);

  // Returns null on failure; the error is recorded against the session so an
  // expanded row can show it.
  const loadMessages = async (sessionId) => {
    const cached = details[sessionId]?.messages;
    if (cached) return cached;

    setDetails((prev) => ({ ...prev, [sessionId]: { loading: true } }));
    try {
      const token = await currentUser.getIdToken();
      const messages = await getConversationMessages(token, sessionId);
      setDetails((prev) => ({
        ...prev,
        [sessionId]: { loading: false, messages },
      }));
      return messages;
    } catch {
      setDetails((prev) => ({
        ...prev,
        [sessionId]: { loading: false, error: true },
      }));
      return null;
    }
  };

  const toggle = async (sessionId) => {
    if (expanded === sessionId) {
      setExpanded(null);
      return;
    }
    setExpanded(sessionId);
    if (details[sessionId]?.loading) return;
    await loadMessages(sessionId);
  };

  // format: "pdf" or "csv".
  const exportConversation = async (conv, format) => {
    const sessionId = conv.session_id;
    if (exporting.includes(sessionId)) return;
    setExporting((cur) => [...cur, sessionId]);
    try {
      const messages = await loadMessages(sessionId);
      if (!messages) {
        onError(
          language === "zh"
            ? "匯出失敗，請重試。"
            : "Export failed. Please try again.",
        );
        return;
      }
      if (format === "pdf") setPrinting({ conv, messages });
      else exportConversationAsCsv(conv, messages, language);
    } finally {
      setExporting((cur) => cur.filter((id) => id !== sessionId));
    }
  };

  return {
    expanded,
    details,
    exporting,
    toggle,
    collapse: () => setExpanded(null),
    exportConversation,
    printing,
    donePrinting: () => setPrinting(null),
  };
}

// ── Popovers ──────────────────────────────────────────────────────────────

// Calls `onOutside` for a press anywhere outside `ref` while `active`. Used
// by the folder switcher, the move menu and the export menu.
export function useOutsidePress(ref, active, onOutside) {
  const callback = useRef(onOutside);
  callback.current = onOutside;
  useEffect(() => {
    if (!active) return;
    const onPress = (event) => {
      if (ref.current && !ref.current.contains(event.target)) callback.current();
    };
    document.addEventListener("mousedown", onPress);
    document.addEventListener("touchstart", onPress);
    return () => {
      document.removeEventListener("mousedown", onPress);
      document.removeEventListener("touchstart", onPress);
    };
  }, [ref, active]);
}
